import { useState, useEffect, useRef } from 'react';
import { Flex, Button, Text, ActionIcon, Loader } from '@mantine/core';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ErrorModal, ModuleView } from '@jadwalh/ui';
import instance from '../../../../../axios-instance';
import { SectionContainer } from './styles';
import { ReactComponent as GMAIL } from '../../../../../assets/images/svg/gmail.svg';
import { useMutation } from '@tanstack/react-query';
import { IconTrash } from '@tabler/icons-react';
import { getProfile } from '../../../../../api/profile.api';
import { useFeatureGate } from '../../../../../hooks/useFeatureGate';
import { BlockedFeatureState } from '../../../../../components/BlockedFeatureState';

interface GmailProps {
  refetch?: () => void;
}

export default function Gmail({ refetch }: GmailProps) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [loading, setIsLoading] = useState(false);
  const [pageLoading, setPageIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const hasFetchedRef = useRef(false); // Track if the fetch has already been done
  const callConnect = useRef(true);
  const { isBlocked, hasAccess } = useFeatureGate('can_use_gmail_integration');
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  const { protocol, hostname, port } = window.location;
  let host = `${protocol}//${hostname}`;
  if (hostname === 'localhost') {
    host += `:${port}`;
  }

  const GOOGLE_REDIRECT_URI = `${host}/settings/integration/activate-gmail`;

  const GOOGLE_AUTH_URL = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${
    import.meta.env.VITE_GOOGLE_CLIENT_ID
  }&redirect_uri=${GOOGLE_REDIRECT_URI}&response_type=code&scope=${encodeURIComponent(
    import.meta.env.VITE_GOOGLE_MAIL_SCOPE
  )}&access_type=offline&prompt=consent`;

  const handleGmailAuth = () => {
    // Check if user has access to Gmail integration
    if (!hasAccess) {
      setShowBlockedModal(true);
      return;
    }

    window.location.href = GOOGLE_AUTH_URL;
  };

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (data: any) => {
      if (!callConnect.current) {
        return Promise.reject('Connection already initiated');
      }
      callConnect.current = false; // Set the flag to false after the first call

      return instance.post('app/integrations/mail/connect', data);
    },
    onSuccess: (data: any) => {
      setIsConnected(true);
      const url = new URL(window.location.href);
      getProfile()
      url.searchParams.set('is_active', 'true');
      window.history.replaceState({}, '', url.toString());
      refetch?.();
    },
    onError: () => {
      window.history.replaceState(
        { path: GOOGLE_REDIRECT_URI },
        '',
        GOOGLE_REDIRECT_URI
      );
    },
  });

  const handleDeactivate = async () => {
    try {
      setIsLoading(true);
      await instance.delete('app/integrations/mail/disconnect');
      setIsConnected(false);
      getProfile()
      window.localStorage.removeItem('gmailEmail');
      refetch?.();
      navigate('/settings/integration/activate-gmail?is_active=false');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return; // If already fetched, do nothing

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const isActive = urlParams.get('is_active'); // This will be 'false' or 'true' as a string
    if (code) {
      hasFetchedRef.current = true; // Set to true to prevent future invocations

      const fetchTokens = async () => {
        const tokenResponse = await fetch(
          'https://oauth2.googleapis.com/token',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              code: code,
              client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
              client_secret: import.meta.env.VITE_GOOGLE_CLIENT_SECRET,
              redirect_uri: GOOGLE_REDIRECT_URI,
              grant_type: 'authorization_code',
            }),
          }
        );
        const tokenData = await tokenResponse.json();
        const accessToken = tokenData.access_token;

        const userInfoResponse = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        const userInfo = await userInfoResponse.json();
        if (userInfo) {
          window.localStorage.setItem('gmailEmail', userInfo.email);
          await mutateAsync({
            email: userInfo.email,
            access_token: accessToken,
            expire_token: tokenData.expires_in,
            refresh_token: tokenData.refresh_token,
            google_id: userInfo.sub,
            first_name: userInfo.given_name,
            last_name: userInfo.family_name,
            group: 'mail',
            key: 'Gmail',
          });
          navigate('/settings/integration/activate-gmail?is_active=true');
        }
      };

      fetchTokens();
      // remove the code params from url
    }
    if (isActive === 'true') {
      setIsConnected(true);
    } else {
      setIsConnected(false);
    }
    setTimeout(() => {
      setPageIsLoading(false);
    }, 500);
  }, []);

  if (pageLoading) {
    return (
      <ModuleView>
        <Flex w={'100%'} my={'100px'} justify="center" align="center">
          <Loader color="#303030" />
        </Flex>
      </ModuleView>
    );
  }

  return (
    <SectionContainer>
      {!isConnected ? (
        <ModuleView>
          <Flex
            direction={{ base: 'column' }}
            align={{ base: 'flex-start' }}
            className="detail_container"
          >
            <Flex justify="center" align="center" gap="10px">
              <GMAIL style={{ width: '20px', height: '20px' }} />
              <p className="SemiBold title">Gmail</p>
            </Flex>
            <Flex direction={'column'} gap={8} w={'100%'} pt={'16px'}>
              <Flex
                direction={'column'}
                align={'flex-start'}
                justify={'flex-start'}
                className="info"
                gap={8}
              >
                <p>{formatMessage({ id: 'integrations.gmail.description' })}</p>
                <ul>
                  <li>
                    {formatMessage({ id: 'integrations.gmail.benefit1' })}
                  </li>
                  <li>
                    {formatMessage({ id: 'integrations.gmail.benefit2' })}
                  </li>
                  <li>
                    {formatMessage({ id: 'integrations.gmail.benefit3' })}
                  </li>
                </ul>

                <Flex direction={'column'} gap={'0'}>
                  <p style={{ marginBottom: '0' }}>
                    {formatMessage({
                      id: 'integrations.gmail.benefit1.title',
                    })}
                  </p>
                  <p>
                    {formatMessage({
                      id: 'integrations.gmail.benefit1.description',
                    })}
                  </p>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </ModuleView>
      ) : (
        <ModuleView>
          <Flex
            direction={{ base: 'column' }}
            align={{ base: 'flex-start' }}
            className="detail_container"
          >
            <Flex justify="center" align="center" gap="10px">
              <p className="SemiBold title">التكامل البريد الإلكتروني</p>
            </Flex>
            <Flex direction={'column'} gap={'16px'} w={'100%'} pt={'16px'}>
              <Flex justify={'space-between'} align={'center'} w={'100%'}>
                <Flex align={'center'} gap={'10px'}>
                  <GMAIL style={{ width: '16px', height: '16px' }} />
                  <Text size="sm" style={{ color: '#666' }}>
                    {window.localStorage.getItem('gmailEmail') ||
                      formatMessage({ id: 'no_email_connected' })}
                  </Text>
                </Flex>
                <ActionIcon
                  variant="subtle"
                  style={{
                    width: '32px',
                    height: '32px',
                    background: 'transparent',
                    color: 'black',
                    margin: '0',
                  }}
                  color="red"
                  size="sm"
                  onClick={handleDeactivate}
                  loading={loading}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Flex>
            </Flex>
          </Flex>
        </ModuleView>
      )}
      {!isConnected && (
        <Button loading={isLoading} onClick={handleGmailAuth}>
          {formatMessage({ id: 'activation' })}
        </Button>
      )}
      {showErrorModal && (
        <ErrorModal
          showErrorModal={showErrorModal}
          setShowErrorModal={setShowErrorModal}
          message={formatMessage({ id: 'integrations.zohoCrm.error' })}
        />
      )}
      {showBlockedModal && (
        <BlockedFeatureState
          title="تكامل Gmail غير متاح في باقتك الحالية"
          description="يتيح لك تكامل Gmail إرسال إشعارات وتأكيدات الاجتماعات تلقائيًا من بريدك، مما يعزّز موثوقية التواصل ويمنح عملاءك تجربة أكثر احترافية."
          onClose={() => setShowBlockedModal(false)}
        />
      )}
    </SectionContainer>
  );
}
